import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from './store';

export interface FlagsState {
  devMode: boolean;
  pttHidden: boolean;
  demoMode: boolean;
}

const FLAGS_KEY = 'appFlags';

const loadFlagsFromSession = (): FlagsState | null => {
  try {
    const s = sessionStorage.getItem(FLAGS_KEY);
    return s ? JSON.parse(s) as FlagsState : null;
  } catch {
    return null;
  }
};

const initialState: FlagsState = loadFlagsFromSession() ?? {
  devMode: import.meta.env.VITE_DEVMODE === 'true',
  pttHidden: false,
  demoMode: false,
};

export const flagsSlice = createSlice({
  name: 'flags',
  initialState,
  reducers: {
    setFlags: (state, action: PayloadAction<Partial<FlagsState>>) => {
      if (typeof action.payload.devMode === 'boolean') state.devMode = action.payload.devMode;
      if (typeof action.payload.pttHidden === 'boolean') state.pttHidden = action.payload.pttHidden;
      if (typeof action.payload.demoMode === 'boolean') state.demoMode = action.payload.demoMode;
      sessionStorage.setItem(FLAGS_KEY, JSON.stringify(state));
    },
    resetFlags: (state) => {
      state.devMode = import.meta.env.VITE_DEVMODE === 'true';
      state.pttHidden = false;
      state.demoMode = false;
      sessionStorage.removeItem(FLAGS_KEY);
    },
  },
  extraReducers: (builder) => {
    // Clear sessionStorage when app state is reset (e.g., on logout)
    // Both reset actions clear flags - auth preferences preservation is handled by rootReducer
    builder
      .addCase('RESET_APP_STATE', () => {
        sessionStorage.removeItem(FLAGS_KEY);
        return {
          devMode: import.meta.env.VITE_DEVMODE === 'true',
          pttHidden: false,
          demoMode: false,
        };
      })
      .addCase('RESET_APP_STATE_PRESERVE_AUTH_PREFS', () => {
        sessionStorage.removeItem(FLAGS_KEY);
        return {
          devMode: import.meta.env.VITE_DEVMODE === 'true',
          pttHidden: false,
          demoMode: false,
        };
      });
  },
});

export const { setFlags, resetFlags } = flagsSlice.actions;

export const selectDevMode = (s: RootState) => s.flags.devMode;
export const selectPttHidden = (s: RootState) => s.flags.pttHidden;
export const selectDemoMode = (s: RootState) => s.flags.demoMode;

export default flagsSlice.reducer;
